import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface RecaptchaVerificationRequest {
  token: string;
  action?: string;
}

export interface RecaptchaVerificationResponse {
  success: boolean;
  score?: number;
  action?: string;
  challenge_ts?: string;
  hostname?: string;
  'error-codes'?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {

  constructor(private http: HttpClient) { }

  /**
   * Verifica el token del reCAPTCHA con el backend
   * @param token Token del reCAPTCHA
   * @param action Acción específica (opcional)
   * @returns Observable con la respuesta de verificación
   */
  verifyToken(token: string, action?: string): Observable<RecaptchaVerificationResponse> {
    const payload: RecaptchaVerificationRequest = { token };
    if (action) {
      payload.action = action;
    }

    return this.http.post<RecaptchaVerificationResponse>(
      `${environment.backendApiUrl}/recaptcha/verify`,
      payload
    );
  }

  /**
   * Verifica si el reCAPTCHA es válido basado en la respuesta
   * @param response Respuesta del servidor
   * @param minScore Score mínimo requerido (para reCAPTCHA v3)
   * @returns true si es válido
   */
  isValid(response: RecaptchaVerificationResponse, minScore: number = 0.5): boolean {
    if (!response.success) {
      return false;
    }

    // Para reCAPTCHA v3, verificar el score
    if (response.score !== undefined) {
      return response.score >= minScore;
    }

    // Para reCAPTCHA v2, solo verificar success
    return true;
  }

  /**
   * Obtiene mensajes de error legibles
   * @param response Respuesta del servidor
   * @returns Array de mensajes de error
   */
  getErrorMessages(response: RecaptchaVerificationResponse): string[] {
    if (!response['error-codes']) {
      return [];
    }

    const errorMessages: { [key: string]: string } = {
      'missing-input-secret': 'Falta la clave secreta del reCAPTCHA',
      'invalid-input-secret': 'Clave secreta del reCAPTCHA inválida',
      'missing-input-response': 'Falta la respuesta del reCAPTCHA',
      'invalid-input-response': 'Respuesta del reCAPTCHA inválida',
      'bad-request': 'Solicitud malformada',
      'timeout-or-duplicate': 'La respuesta del reCAPTCHA ha expirado o es duplicada'
    };

    return response['error-codes'].map(code => errorMessages[code] || `Error desconocido: ${code}`);
  }
}

